import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { rewriteNotesViaAI, cleanupDictationViaAI } from '../../../services/aiService';
import { referralService } from '../../../services/referralService';


const ClinicalNotesSection = ({ notes, onChange, error, toothNumbers = [], referralReasons = [], onAnalysisComplete, practitionerSlug, formData }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [suggestionError, setSuggestionError] = useState(null);
  const [draftReferralId, setDraftReferralId] = useState(null);
  const [draftPublicToken, setDraftPublicToken] = useState(null);
  const recognitionRef = useRef(null);
  const isStoppingRef = useRef(false);
  const placeholderText = "Entrez vos observations cliniques détaillées, symptômes, résultats diagnostiques et tout antécédent pertinent...";

  /**
   * Create draft referral if not already created
   * This is needed to get referral_id + public_token for AI operations
   */
  const ensureDraftReferral = async () => {
    // If we already have a draft, return it
    if (draftReferralId && draftPublicToken) {
      return { id: draftReferralId, publicToken: draftPublicToken };
    }

    // Validate minimum required data
    if (!practitionerSlug) {
      throw new Error('Données du praticien manquantes');
    }

    if (!formData?.patientInfo?.firstName || !formData?.patientInfo?.lastName || !formData?.referringDentist?.fullName) {
      throw new Error('Veuillez d\'abord remplir les informations du patient et du dentiste référent.');
    }

    // Create draft referral
    console.log('🚀 Creating draft referral for AI operations...');
    const result = await referralService?.create({
      practitionerSlug,
      patientInfo: formData?.patientInfo,
      referringDentist: formData?.referringDentist,
      toothNumbers: formData?.toothNumbers || [],
      referralReasons: formData?.referralReasons || [],
      clinicalNotes: formData?.clinicalNotes || '',
      imaging: formData?.imaging || {},
    });

    if (!result?.id || !result?.publicToken) {
      throw new Error('Impossible de créer le brouillon d\'adressage');
    }

    console.log('✅ Draft referral created:', result?.id);
    setDraftReferralId(result?.id);
    setDraftPublicToken(result?.publicToken);

    return { id: result?.id, publicToken: result?.publicToken };
  };

  useEffect(() => {
    // Initialize Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';

      recognitionRef.current.onresult = async (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event?.resultIndex; i < event?.results?.length; i++) {
          const transcript = event?.results?.[i]?.[0]?.transcript;
          if (event?.results?.[i]?.isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Update with interim results
        if (interimTranscript) {
          onChange((notes || '') + interimTranscript);
        }

        // When we have final transcript, clean it up via AI
        if (finalTranscript?.trim()) {
          const rawText = (notes || '') + finalTranscript;
          onChange(rawText);
          
          // Auto-cleanup the dictated text via AI
          try {
            setIsGenerating(true);
            
            // Ensure we have a draft referral
            const draft = await ensureDraftReferral();
            
            // Call AI service via Edge Function
            const cleanedText = await cleanupDictationViaAI(
              draft?.id,
              draft?.publicToken,
              rawText,
              toothNumbers,
              referralReasons
            );
            onChange(cleanedText);
          } catch (error) {
            // Keep the raw dictated text if cleanup fails
            console.error('Dictation cleanup error:', error);
            setSuggestionError(error?.message || 'Erreur lors du nettoyage de la dictée');
          } finally {
            setIsGenerating(false);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event?.error);
        setIsDictating(false);
        isStoppingRef.current = false;
        
        if (event?.error === 'not-allowed') {
          setSuggestionError('Accès au microphone refusé. Veuillez autoriser l\'accès au microphone.');
        } else if (event?.error === 'no-speech') {
          setSuggestionError('Aucune parole détectée. Veuillez réessayer.');
        } else {
          setSuggestionError('Erreur de reconnaissance vocale. Veuillez réessayer.');
        }
      };

      recognitionRef.current.onend = () => {
        // Only update state if we're not in the middle of a manual stop
        if (!isStoppingRef?.current) {
          setIsDictating(false);
        }
        isStoppingRef.current = false;
      };

      recognitionRef.current.onstart = () => {
        setIsDictating(true);
        isStoppingRef.current = false;
      };
    }

    return () => {
      if (recognitionRef?.current) {
        try {
          recognitionRef?.current?.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [toothNumbers, referralReasons, practitionerSlug, formData]);

  const handleChange = (e) => {
    onChange(e?.target?.value);
  };

  const handleGenerateSuggestion = async () => {
    if (!notes?.trim()) {
      setSuggestionError('Veuillez d\'abord écrire vos observations pour que l\'IA puisse les reformuler.');
      return;
    }

    setIsGenerating(true);
    setSuggestionError(null);

    try {
      // Ensure we have a draft referral
      const draft = await ensureDraftReferral();

      // Call AI service via Edge Function
      const reformulatedText = await rewriteNotesViaAI(
        draft?.id,
        draft?.publicToken,
        notes,
        toothNumbers,
        referralReasons
      );
      onChange(reformulatedText);
    } catch (error) {
      setSuggestionError(error?.message || 'Erreur lors de la reformulation.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDictation = () => {
    if (!recognitionRef?.current) {
      setSuggestionError('La reconnaissance vocale n\'est pas supportée par votre navigateur.');
      return;
    }

    setSuggestionError(null);

    if (isDictating) {
      // User wants to stop
      isStoppingRef.current = true;
      setIsDictating(false);
      try {
        recognitionRef?.current?.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        isStoppingRef.current = false;
      }
    } else {
      // User wants to start
      try {
        recognitionRef?.current?.start();
        // State will be set by onstart event
      } catch (error) {
        console.error('Error starting recognition:', error);
        setSuggestionError('Impossible de démarrer la dictée. Veuillez réessayer.');
        setIsDictating(false);
      }
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6 hover-glow hover-lift">
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-white mb-1">
          Notes cliniques
        </h2>
        <p className="text-sm text-gray-400">
          Décrivez les observations cliniques pertinentes
        </p>
      </div>
      
      <div className="mb-4 flex flex-wrap gap-3">
        <Button
          variant="outline"
          size="sm"
          iconName="Sparkles"
          iconPosition="left"
          onClick={handleGenerateSuggestion}
          disabled={isGenerating || isDictating}
          className="text-sm bg-purple-500/10 border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:border-purple-500/50 hover:text-purple-200 transition-all duration-300"
        >
          {isGenerating ? 'Reformulation...' : 'Reformuler avec l\'IA'}
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          iconName={isDictating ? "MicOff" : "Mic"}
          iconPosition="left"
          onClick={handleDictation}
          disabled={isGenerating}
          className={`text-sm transition-all duration-300 ${
            isDictating 
              ? 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30 animate-pulse' :'hover:bg-primary/10 hover:border-primary/50'
          }`}
        >
          {isDictating ? 'Arrêter la dictée' : 'Dicter avec l\'IA'}
        </Button>
        
        {suggestionError && (
          <div className="w-full mt-1 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
            {suggestionError}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <textarea
          value={notes}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={!isFocused && !notes ? placeholderText : ''}
          className={`w-full px-4 py-3 bg-[rgba(245,245,247,0.95)] border rounded-2xl text-[#1A1A1A] font-medium text-base resize-vertical min-h-[180px] focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/30 focus:shadow-[0_0_20px_rgba(74,144,226,0.2)] transition-all placeholder:text-[#8E8E93] ${
            error ? 'border-red-400/60' : 'border-white/20'
          }`}
          style={{ fontSize: '16px' }}
          rows={7}
        />
        {error && (
          <p className="text-sm text-red-400">{error}</p>
        )}
        <div className="text-xs text-gray-400 text-right">
          {notes?.length} caractères
        </div>
      </div>
    </div>
  );
};

export default ClinicalNotesSection;